import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { materialService } from '../services/material-service';
import {
  Material,
  CreateMaterialData,
  UpdateMaterialData,
  MaterialTariffHistory,
  CreateTariffData,
} from '../types/material.types';

interface MaterialState {
  materials: Material[];
  isLoading: boolean;
  error: string | null;

  fetchMaterials: () => Promise<void>;
  createMaterial: (data: CreateMaterialData) => Promise<Material>;
  updateMaterial: (id: string, data: UpdateMaterialData) => Promise<Material>;
  deleteMaterial: (id: string) => Promise<void>;
  addTariff: (materialId: string, data: CreateTariffData) => Promise<Material>;
  getTariffHistory: (materialId: string) => Promise<MaterialTariffHistory[]>;
  clearError: () => void;
}

export const useMaterialStore = create<MaterialState>()(
  devtools(
    (set) => ({
      materials: [],
      isLoading: false,
      error: null,

      fetchMaterials: async () => {
        try {
          set({ isLoading: true, error: null });
          const materials = await materialService.getMaterials();
          set({ materials, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al cargar materiales',
            isLoading: false,
          });
          throw error;
        }
      },

      createMaterial: async (data) => {
        try {
          set({ isLoading: true, error: null });
          const material = await materialService.createMaterial(data);
          set((state) => ({
            materials: [...state.materials, material],
            isLoading: false,
          }));
          return material;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al crear material',
            isLoading: false,
          });
          throw error;
        }
      },

      updateMaterial: async (id, data) => {
        try {
          set({ isLoading: true, error: null });
          const material = await materialService.updateMaterial(id, data);
          set((state) => ({
            materials: state.materials.map((m) => (m.id === id ? material : m)),
            isLoading: false,
          }));
          return material;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al actualizar material',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteMaterial: async (id) => {
        try {
          set({ isLoading: true, error: null });
          await materialService.deleteMaterial(id);
          set((state) => ({
            materials: state.materials.filter((m) => m.id !== id),
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al eliminar material',
            isLoading: false,
          });
          throw error;
        }
      },

      addTariff: async (materialId, data) => {
        try {
          set({ isLoading: true, error: null });
          const material = await materialService.addTariff(materialId, data);
          set((state) => ({
            materials: state.materials.map((m) => (m.id === materialId ? material : m)),
            isLoading: false,
          }));
          return material;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al agregar tarifa',
            isLoading: false,
          });
          throw error;
        }
      },

      getTariffHistory: async (materialId) => {
        return await materialService.getTariffHistory(materialId);
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    { name: 'MaterialStore' }
  )
);

export default useMaterialStore;
